import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, HeartPulse, ChevronDown } from "lucide-react";
import { NAV, CLINIC } from "@/data/site";

function DesktopItem({ item }) {
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const [open, setOpen] = useState(false);

  if (!hasChildren) {
    return (
      <NavLink
        to={item.to}
        data-testid={`nav-link-${item.to === "/" ? "home" : item.to.replace("/", "")}`}
        className={({ isActive }) =>
          `relative text-sm font-medium tracking-wide transition-colors duration-300 hover:text-brand ${
            isActive ? "text-brand" : "text-ink/70"
          }`
        }
      >
        {item.label}
      </NavLink>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <NavLink
        to={item.to}
        data-testid={`nav-link-${item.to.replace("/", "")}`}
        className={({ isActive }) =>
          `flex items-center gap-1 text-sm font-medium tracking-wide transition-colors duration-300 hover:text-brand ${
            isActive ? "text-brand" : "text-ink/70"
          }`
        }
      >
        {item.label}
        <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </NavLink>
      <div
        data-testid={`nav-dropdown-${item.to.replace("/", "")}`}
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 z-50 transition-[opacity,transform] duration-200 ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
      >
        <div className="w-72 rounded-2xl bg-paper/95 backdrop-blur-xl border border-line shadow-xl shadow-brand/5 p-2">
          {item.children.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className="block rounded-xl px-4 py-3 text-sm text-ink/80 hover:bg-mint hover:text-brand-deep transition-colors duration-200"
            >
              {c.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expanded, setExpanded] = useState(null); // mobile dropdown parent
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setExpanded(null);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-paper/80 backdrop-blur-xl border-b border-line/60 py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <span className="grid place-items-center w-9 h-9 rounded-full bg-brand text-white transition-colors duration-300 group-hover:bg-brand-dark">
            <HeartPulse size={18} strokeWidth={2.2} />
          </span>
          <span className="leading-none">
            <span className="block font-display text-xl md:text-2xl font-semibold text-ink tracking-tight">
              Dr. Pallavi Mishra
            </span>
            <span className="block text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-sage font-semibold mt-0.5">
              Cardiologist · Varanasi
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => (
            <DesktopItem key={item.to} item={item} />
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:+${CLINIC.phoneRaw}`}
            data-testid="nav-book-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-dark text-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.16em] transition-colors duration-300"
          >
            <Phone size={15} /> Book Now
          </a>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid place-items-center w-11 h-11 rounded-full border border-line text-ink"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden fixed inset-0 top-[0] z-40 bg-paper transition-[opacity,transform] duration-500 ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-4 pointer-events-none"
        }`}
        data-testid="mobile-menu"
      >
        <div className="h-full overflow-y-auto pt-24 pb-10 px-8">
          <div className="flex flex-col gap-1">
            {NAV.map((item) => {
              const hasChildren = Array.isArray(item.children) && item.children.length > 0;
              const isExpanded = expanded === item.to;
              return (
                <div key={item.to}>
                  <div className="flex items-center justify-between">
                    <NavLink
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `font-display text-3xl sm:text-4xl py-2 tracking-tight transition-colors duration-300 ${
                          isActive ? "text-brand" : "text-ink"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                    {hasChildren && (
                      <button
                        onClick={() => setExpanded(isExpanded ? null : item.to)}
                        aria-label="Toggle submenu"
                        className="grid place-items-center w-10 h-10 rounded-full border border-line text-ink"
                        data-testid={`mobile-submenu-toggle-${item.to.replace("/", "")}`}
                      >
                        <ChevronDown size={16} className={`transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                  {hasChildren && isExpanded && (
                    <div className="pl-4 mt-1 mb-3 space-y-1 border-l border-line">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          onClick={() => setOpen(false)}
                          className="block py-2 text-base text-sage hover:text-brand transition-colors"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <a
            href={`tel:+${CLINIC.phoneRaw}`}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-sm font-semibold uppercase tracking-[0.16em] w-max"
          >
            <Phone size={16} /> {CLINIC.phone}
          </a>
        </div>
      </div>
    </header>
  );
}
